import { useEffect } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useSyncExternalStore } from 'use-sync-external-store/shim';

import { HOSTNAME_QUERY_OPTIONS } from '../constants/qzQueries.const';
import { useShippoPrint } from './useShippoPrint/useShippoPrint';

type PrintSetupStatus = 'connected' | 'idle' | 'setup_attempted' | 'setup_expired';

const STORAGE_KEY = 'shippo-print-setup';
const SETUP_CONNECT_RETRY_INTERVAL_MS = 1_000;
const SETUP_ATTEMPT_DURATION_MS = 15 * 60 * 1_000;

type PersistedPrintSetupState = {
  lastConnectedAt: null | number;
  lastKnownHostname: null | string;
  lastSetupAttemptedAt: null | number;
  setupAttemptExpiresAt: null | number;
};

type PrintSetupState = PersistedPrintSetupState;

type DerivedPrintSetupState = {
  hasActiveSetupAttempt: boolean;
  hasCompletedSetup: boolean;
  hasExpiredSetupAttempt: boolean;
  isKnownInstallation: boolean;
  setupAttemptRemainingMs: number;
  status: PrintSetupStatus;
};

const EMPTY_SETUP_STATE: PersistedPrintSetupState = {
  lastConnectedAt: null,
  lastKnownHostname: null,
  lastSetupAttemptedAt: null,
  setupAttemptExpiresAt: null,
};

const numberOrNull = (value: unknown): null | number =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const stringOrNull = (value: unknown): null | string =>
  typeof value === 'string' && value.length > 0 ? value : null;

const readPersistedSetup = (): PersistedPrintSetupState => {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_SETUP_STATE;

    const parsed = JSON.parse(raw) as Partial<PersistedPrintSetupState>;
    const lastConnectedAt = numberOrNull(parsed.lastConnectedAt);
    const lastSetupAttemptedAt = numberOrNull(parsed.lastSetupAttemptedAt);
    const savedExpiresAt = numberOrNull(parsed.setupAttemptExpiresAt);

    return {
      lastConnectedAt,
      lastKnownHostname: stringOrNull(parsed.lastKnownHostname),
      lastSetupAttemptedAt,
      setupAttemptExpiresAt:
        lastConnectedAt !== null
          ? null
          : savedExpiresAt ??
            (lastSetupAttemptedAt === null
              ? null
              : lastSetupAttemptedAt + SETUP_ATTEMPT_DURATION_MS),
    };
  } catch {
    return EMPTY_SETUP_STATE;
  }
};

// Module-level singleton: one persisted setup record shared across all hook consumers,
// hydrated from localStorage on first import so a refresh resumes the same setup window.
let setupState: PrintSetupState = {
  ...readPersistedSetup(),
};

const listeners = new Set<() => void>();

const emit = () => {
  for (const listener of listeners) listener();
};

const persistSetup = (next: PrintSetupState) => {
  try {
    const persisted: PersistedPrintSetupState = {
      lastConnectedAt: next.lastConnectedAt,
      lastKnownHostname: next.lastKnownHostname,
      lastSetupAttemptedAt: next.lastSetupAttemptedAt,
      setupAttemptExpiresAt: next.setupAttemptExpiresAt,
    };
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(persisted));
  } catch {
    // localStorage is best-effort for setup continuity across refreshes.
  }
};

const updateSetup = (patch: Partial<PrintSetupState>) => {
  const next = { ...setupState, ...patch };
  const changed = (Object.keys(patch) as (keyof PrintSetupState)[]).some(
    (key) => setupState[key] !== next[key],
  );

  if (!changed) return;

  setupState = next;
  persistSetup(setupState);
  emit();
};

const deriveSetup = (
  state: PrintSetupState,
  isConnected: boolean,
  now = Date.now(),
): DerivedPrintSetupState => {
  const hasCompletedSetup = isConnected;
  const setupAttemptRemainingMs =
    state.setupAttemptExpiresAt === null ? 0 : Math.max(0, state.setupAttemptExpiresAt - now);
  const hasActiveSetupAttempt = !hasCompletedSetup && setupAttemptRemainingMs > 0;
  const hasExpiredSetupAttempt =
    !hasCompletedSetup &&
    state.lastSetupAttemptedAt !== null &&
    state.setupAttemptExpiresAt !== null &&
    setupAttemptRemainingMs === 0;

  return {
    hasActiveSetupAttempt,
    hasCompletedSetup,
    hasExpiredSetupAttempt,
    isKnownInstallation:
      hasCompletedSetup || state.lastConnectedAt !== null || state.lastKnownHostname !== null,
    setupAttemptRemainingMs,
    status: hasCompletedSetup
      ? 'connected'
      : hasActiveSetupAttempt
      ? 'setup_attempted'
      : hasExpiredSetupAttempt
      ? 'setup_expired'
      : 'idle',
  };
};

const completeSetup = (connectedAt = Date.now()) => {
  updateSetup({
    lastConnectedAt: connectedAt,
    setupAttemptExpiresAt: null,
  });
};

const resetSetupState = () => {
  updateSetup({ ...EMPTY_SETUP_STATE });
  try {
    globalThis.localStorage?.removeItem(STORAGE_KEY);
  } catch {
    // localStorage is best-effort for setup continuity across refreshes.
  }
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = () => setupState;

export const usePrintSetup = () => {
  const state = useSyncExternalStore(subscribe, getSnapshot);
  const queryClient = useQueryClient();
  const {
    connect: connectShippoPrint,
    connected,
    disconnect: disconnectShippoPrint,
    isEnsuringConnection,
    lastErrorCode,
    status: connectionStatus,
  } = useShippoPrint();

  useEffect(() => {
    if (connected) completeSetup();
  }, [connected]);

  useEffect(() => {
    const setup = deriveSetup(state, connected);
    if (
      isEnsuringConnection ||
      !setup.hasActiveSetupAttempt ||
      state.setupAttemptExpiresAt === null
    )
      return;

    connectShippoPrint({
      reason: 'setup',
      retryIntervalMs: SETUP_CONNECT_RETRY_INTERVAL_MS,
      retryWindowMs: state.setupAttemptExpiresAt - Date.now(),
    });
  }, [connectShippoPrint, connected, isEnsuringConnection, state]);

  useEffect(() => {
    if (!connected) return;

    let cancelled = false;
    void queryClient
      .fetchQuery(HOSTNAME_QUERY_OPTIONS)
      .then((hostname) => {
        const nextHostname = stringOrNull(hostname);
        if (!cancelled && nextHostname !== null) updateSetup({ lastKnownHostname: nextHostname });
      })
      .catch(() => {
        // Keep the previous hostname when this diagnostic call fails.
      });

    return () => {
      cancelled = true;
    };
  }, [connected, queryClient]);

  const attemptSetup = () => {
    if (connected) {
      completeSetup();
      return;
    }

    const now = Date.now();
    const setupAttemptExpiresAt = now + SETUP_ATTEMPT_DURATION_MS;
    updateSetup({
      lastSetupAttemptedAt: now,
      setupAttemptExpiresAt,
    });
    connectShippoPrint({
      reason: 'setup',
      retryIntervalMs: SETUP_CONNECT_RETRY_INTERVAL_MS,
      retryWindowMs: SETUP_ATTEMPT_DURATION_MS,
    });
  };

  const resetSetup = () => {
    resetSetupState();
    disconnectShippoPrint();
  };

  const setup = deriveSetup(state, connected);

  return {
    ...setup,
    ...state,
    attemptSetup,
    connectionStatus,
    hasKnownConnectionIssue:
      setup.isKnownInstallation && !connected && !isEnsuringConnection && lastErrorCode !== null,
    isPolling: isEnsuringConnection,
    resetSetup,
  };
};
